import { useState, useEffect } from "react";
import { getUsers, getRoles, getRoleHierarchy, getUserPermissions, updateUserPermissions, createRole, updateRole, deleteRole, updateUser } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, 
  Users, 
  Plus, 
  Trash2, 
  ChevronDown,
  ChevronRight,
  User,
  Settings,
  Loader2,
  AlertCircle,
  Crown,
  ShieldCheck,
  UserCog,
  Eye
} from "lucide-react";

// Role display configuration
const ROLE_CONFIG = {
  master_admin: { 
    label: "Master Admin", 
    icon: Crown, 
    color: "bg-yellow-500", 
    textColor: "text-yellow-700",
    badgeClass: "bg-yellow-100 text-yellow-800 border-yellow-300"
  },
  admin: { 
    label: "Admin", 
    icon: ShieldCheck, 
    color: "bg-blue-500", 
    textColor: "text-blue-700",
    badgeClass: "bg-blue-100 text-blue-800 border-blue-300"
  },
  operational_agent: { 
    label: "Operational Agent", 
    icon: UserCog, 
    color: "bg-green-500", 
    textColor: "text-green-700",
    badgeClass: "bg-green-100 text-green-800 border-green-300"
  },
  exposition: { 
    label: "Exposition (Demo)", 
    icon: Eye, 
    color: "bg-amber-500", 
    textColor: "text-amber-700",
    badgeClass: "bg-amber-100 text-amber-800 border-amber-300"
  },
  staff: { 
    label: "Operational Agent", 
    icon: UserCog, 
    color: "bg-green-500", 
    textColor: "text-green-700",
    badgeClass: "bg-green-100 text-green-800 border-green-300"
  }
};

// Define all sections and their available actions
const SECTIONS_CONFIG = {
  cargo: {
    label: "Cargo Module",
    actions: [
      { key: "view", label: "Access Cargo Module" },
      { key: "create", label: "Create Entries / Services / Agencies" },
      { key: "edit", label: "Edit Entries / Services / Agencies" },
      { key: "delete", label: "Delete Entries / Services / Agencies" }
    ]
  },
  dashboard: {
    label: "Dashboard",
    actions: [{ key: "view", label: "View Dashboard" }]
  },
  global_planning: {
    label: "Global Planning",
    actions: [
      { key: "view", label: "View Planning" },
      { key: "inline_edit", label: "Inline Edit (Time, PAX, Parking)" }
    ]
  },
  flights: {
    label: "Flights Management",
    actions: [
      { key: "view", label: "View Flights" },
      { key: "create", label: "Create Flight" },
      { key: "edit", label: "Edit Flight" },
      { key: "delete", label: "Delete/Cancel Flight" },
      { key: "inline_edit", label: "Inline Edit" }
    ]
  },
  canceled_flights: {
    label: "Canceled Flights",
    actions: [
      { key: "view", label: "View Canceled Flights" },
      { key: "restore", label: "Restore Flight" },
      { key: "delete_permanently", label: "Delete Single Canceled Flight" },
      { key: "delete_all", label: "Delete ALL Canceled Flights" }
    ]
  },
  statistics: {
    label: "Statistics",
    actions: [{ key: "view", label: "View Statistics" }]
  },
  services: {
    label: "Services Management",
    actions: [
      { key: "view", label: "View Services" },
      { key: "create", label: "Add New Service" },
      { key: "edit", label: "Edit/Toggle Services" },
      { key: "delete", label: "Delete Services" }
    ]
  },
  aircraft_data: {
    label: "Aircraft Data",
    actions: [
      { key: "view", label: "View Aircraft Data" },
      { key: "add", label: "Add Aircraft" },
      { key: "edit", label: "Edit Aircraft" },
      { key: "delete", label: "Delete Single Aircraft" },
      { key: "delete_all", label: "Delete ALL Aircraft" },
      { key: "upload", label: "Upload XLS" },
      { key: "export", label: "Export XLS" },
      { key: "merge", label: "Merge Operators" }
    ]
  },
  airport_data: {
    label: "Airport Data",
    actions: [
      { key: "view", label: "View Airport Data" },
      { key: "add", label: "Add Airport" },
      { key: "edit", label: "Edit Airport" },
      { key: "delete", label: "Delete Single Airport" },
      { key: "delete_all", label: "Delete ALL Airports" },
      { key: "upload", label: "Upload XLS" },
      { key: "export", label: "Export XLS" }
    ]
  },
  import_export: {
    label: "Import/Export Flights",
    actions: [
      { key: "view", label: "View Import/Export" },
      { key: "import", label: "Import Flights" },
      { key: "export", label: "Export Flights" },
      { key: "delete_all", label: "Delete All Flights" }
    ]
  },
  users: {
    label: "User Management",
    actions: [
      { key: "view", label: "View Users" },
      { key: "create", label: "Add User" },
      { key: "edit", label: "Edit User" },
      { key: "delete", label: "Delete User" },
      { key: "activate", label: "Activate/Deactivate User" }
    ]
  },
  login_history: {
    label: "Login History",
    actions: [
      { key: "view", label: "View Login History" },
      { key: "delete", label: "Delete Login History" },
      { key: "manage_sessions", label: "View Active Sessions & Auto-Logout Settings" },
      { key: "force_logout", label: "Force Logout All Users" }
    ]
  },
  settings: {
    label: "Settings",
    actions: [
      { key: "view", label: "View Settings" },
      { key: "edit", label: "Edit Settings" }
    ]
  },
  permissions: {
    label: "Permissions",
    actions: [
      { key: "view", label: "View Permissions" },
      { key: "edit", label: "Edit Permissions" }
    ]
  }
};

const PermissionsPage = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleHierarchy, setRoleHierarchy] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState("users"); // "users" or "roles"
  const [newRoleName, setNewRoleName] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes, hierarchyRes] = await Promise.all([
        getUsers(),
        getRoles(),
        getRoleHierarchy()
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setRoleHierarchy(hierarchyRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      const response = await getUserPermissions(userId);
      setUserPermissions(response.data);
      // Expand all sections by default
      const expanded = {};
      Object.keys(SECTIONS_CONFIG).forEach(key => expanded[key] = true);
      setExpandedSections(expanded);
    } catch (error) {
      toast.error("Failed to load user permissions");
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    await loadUserPermissions(user.id);
  };

  const handleToggleUserActive = async (user, isActive) => {
    try {
      await updateUser(user.id, { is_active: isActive });
      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === user.id ? { ...u, is_active: isActive } : u
      ));
      if (selectedUser?.id === user.id) {
        setSelectedUser({ ...selectedUser, is_active: isActive });
      }
      toast.success(`User ${isActive ? "activated" : "deactivated"}`);
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getEffectivePermission = (section, action) => {
    if (!userPermissions) return false;
    
    // Check override first
    const override = userPermissions.overrides?.[section]?.[action];
    if (override !== undefined) return override;
    
    // Fall back to base permission
    return userPermissions.base_permissions?.sections?.[section]?.[action] || false;
  };

  const hasOverride = (section, action) => {
    return userPermissions?.overrides?.[section]?.[action] !== undefined;
  };

  const handlePermissionChange = async (section, action, checked) => {
    // Update local state first for immediate feedback
    const newOverrides = { ...userPermissions.overrides };
    if (!newOverrides[section]) {
      newOverrides[section] = {};
    }
    newOverrides[section][action] = checked;
    
    setUserPermissions(prev => ({
      ...prev,
      overrides: newOverrides
    }));
    
    // Auto-save immediately
    try {
      setSaving(true);
      await updateUserPermissions(selectedUser.id, {
        permissions: newOverrides
      });
      toast.success("Permission updated");
    } catch (error) {
      toast.error("Failed to save permission");
      // Revert on error
      await loadUserPermissions(selectedUser.id);
    } finally {
      setSaving(false);
    }
  };

  const resetToRole = async (section, action) => {
    const newOverrides = { ...userPermissions.overrides };
    if (newOverrides[section]) {
      delete newOverrides[section][action];
      if (Object.keys(newOverrides[section]).length === 0) {
        delete newOverrides[section];
      }
    }
    
    setUserPermissions(prev => ({
      ...prev,
      overrides: newOverrides
    }));
    
    // Auto-save immediately
    try {
      setSaving(true);
      await updateUserPermissions(selectedUser.id, {
        permissions: newOverrides
      });
      toast.success("Permission reset to role default");
    } catch (error) {
      toast.error("Failed to reset permission");
      await loadUserPermissions(selectedUser.id);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      toast.error("Please enter a role name");
      return;
    }
    
    try {
      await createRole({
        name: newRoleName,
        description: "",
        permissions: {
          sections: Object.fromEntries(
            Object.keys(SECTIONS_CONFIG).map(key => [
              key,
              Object.fromEntries(
                SECTIONS_CONFIG[key].actions.map(a => [a.key, false])
              )
            ])
          )
        }
      });
      toast.success("Role created successfully");
      setNewRoleName("");
      loadData();
    } catch (error) {
      toast.error("Failed to create role");
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm("Are you sure you want to delete this role?")) return;
    
    try {
      await deleteRole(roleId);
      toast.success("Role deleted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete role");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="permissions-page">
      <div className="sticky-page-header mb-6">
        <h1 className="text-2xl font-bold text-[#0f172a] dark:text-white flex items-center gap-3">
          <Shield className="w-7 h-7 text-[#d4af37]" />
          Permission Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Manage user permissions and roles</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === "users" ? "default" : "outline"}
          onClick={() => setActiveTab("users")}
          className={activeTab === "users" ? "bg-[#0f172a] text-white" : ""}
          data-testid="tab-users"
        >
          <Users className="w-4 h-4 mr-2" />
          User Permissions
        </Button>
        <Button
          variant={activeTab === "roles" ? "default" : "outline"}
          onClick={() => setActiveTab("roles")}
          className={activeTab === "roles" ? "bg-[#0f172a] text-white" : ""}
          data-testid="tab-roles"
        >
          <Settings className="w-4 h-4 mr-2" />
          Roles
        </Button>
      </div>

      {/* Role Hierarchy Info */}
      {roleHierarchy && (
        <div className="mb-6 p-4 bg-gradient-to-r from-[#0f172a] to-[#1e293b] rounded-xl text-white">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#d4af37]" />
            Role Hierarchy
          </h3>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400 font-medium">Master Admin</span>
              <span className="text-gray-400 text-sm">(Full Control)</span>
            </div>
            <span className="text-gray-500">→</span>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-400" />
              <span className="text-blue-400 font-medium">Admin</span>
              <span className="text-gray-400 text-sm">(Manage Agents)</span>
            </div>
            <span className="text-gray-500">→</span>
            <div className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">Operational Agent</span>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            You are: <span className={`font-medium ${roleHierarchy.is_master_admin ? 'text-yellow-400' : roleHierarchy.current_role === 'admin' ? 'text-blue-400' : 'text-green-400'}`}>
              {roleHierarchy.is_master_admin ? 'Master Admin' : ROLE_CONFIG[roleHierarchy.current_role]?.label || roleHierarchy.current_role}
            </span>
            {roleHierarchy.creatable_roles?.length > 0 && (
              <span className="ml-2">| Can create: {roleHierarchy.creatable_roles.map(r => ROLE_CONFIG[r]?.label || r).join(', ')}</span>
            )}
          </p>
        </div>
      )}

      {activeTab === "users" && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* User List */}
          <div className="lg:col-span-1 bg-white dark:bg-[#1e293b] rounded-xl shadow-sm border border-gray-200 dark:border-[#334155] overflow-hidden">
            <div className="p-4 bg-[#0f172a] text-white">
              <h2 className="font-semibold flex items-center gap-2">
                <Users className="w-4 h-4" />
                Users
              </h2>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-[#334155] max-h-[600px] overflow-y-auto">
              {users.map(user => {
                const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.operational_agent;
                const RoleIcon = roleConfig.icon;
                const isMasterAdmin = user.role === 'master_admin';
                const isAdmin = user.role === 'admin';
                const canToggleActive = roleHierarchy?.is_master_admin || (!isMasterAdmin && !isAdmin);
                
                return (
                  <div
                    key={user.id}
                    className={`p-4 hover:bg-gray-50 dark:hover:bg-[#334155] transition-colors ${
                      selectedUser?.id === user.id ? "bg-blue-50 dark:bg-[#334155] border-l-4 border-[#d4af37]" : ""
                    }`}
                    data-testid={`user-item-${user.id}`}
                  >
                    <div 
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => handleSelectUser(user)}
                    >
                      <div className={`w-10 h-10 ${user.is_active ? roleConfig.color : 'bg-gray-400'} rounded-full flex items-center justify-center text-white font-bold`}>
                        {isMasterAdmin ? <Crown className="w-5 h-5" /> : user.full_name?.charAt(0) || "U"}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${user.is_active ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                          {user.full_name}
                          {isMasterAdmin && <Crown className="w-4 h-4 inline ml-1 text-yellow-500" />}
                        </p>
                        <Badge className={`text-xs ${roleConfig.badgeClass} border`}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {roleConfig.label}
                        </Badge>
                      </div>
                    </div>
                    {/* Active Toggle */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-[#334155]">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Active</span>
                      <Switch
                        checked={user.is_active}
                        onCheckedChange={(checked) => handleToggleUserActive(user, checked)}
                        disabled={!canToggleActive}
                        data-testid={`user-active-toggle-${user.id}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Permission Editor */}
          <div className="lg:col-span-3">
            {selectedUser ? (
              <div className="bg-white dark:bg-[#1e293b] rounded-xl shadow-sm border border-gray-200 dark:border-[#334155]">
                <div className="p-4 bg-[#0f172a] text-white flex justify-between items-center">
                  <div>
                    <h2 className="font-semibold flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {selectedUser.full_name}
                      {userPermissions?.is_master_admin && <Crown className="w-4 h-4 text-yellow-400" />}
                    </h2>
                    <p className="text-sm text-gray-300">
                      Role: <span className={`capitalize font-medium ${userPermissions?.is_master_admin ? 'text-yellow-400' : userPermissions?.role === 'admin' ? 'text-blue-400' : 'text-green-400'}`}>
                        {ROLE_CONFIG[userPermissions?.role]?.label || userPermissions?.role}
                      </span>
                      {userPermissions?.role === "admin" && " (Full Access)"}
                    </p>
                  </div>
                  {saving && (
                    <div className="flex items-center gap-2 text-[#d4af37]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Saving...</span>
                    </div>
                  )}
                </div>

                {userPermissions?.is_master_admin ? (
                  <div className="p-8 text-center">
                    <Crown className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-300 font-medium">Master Admin - Full Unrestricted Access</p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">This is the protected Master Admin account with complete system control.</p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">Permissions cannot be modified.</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-2 max-h-[600px] overflow-y-auto">
                    {/* Exposition (Demo / visual-only) role banner */}
                    {userPermissions?.is_exposition && (
                      <div className="mb-4 p-3 rounded-lg border bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700">
                        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                          <Eye className="w-5 h-5" />
                          <span className="font-medium">Exposition (Demo) Role</span>
                        </div>
                        <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                          This user sees every page and button like an admin, but cannot edit, save, delete, import or export — any modification shows a "visual only" popup. To change this, edit the user and pick a different role in User Management.
                        </p>
                      </div>
                    )}
                    {/* Info banner for Admin */}
                    {userPermissions?.role === "admin" && (
                      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                          <ShieldCheck className="w-5 h-5" />
                          <span className="font-medium">Admin User</span>
                        </div>
                        <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                          Admins have full access by default. You can restrict specific permissions below.
                        </p>
                      </div>
                    )}
                    {/* Permission Sections */}
                    {!userPermissions?.is_exposition && Object.entries(SECTIONS_CONFIG).map(([sectionKey, sectionConfig]) => (
                      <div key={sectionKey} className="border border-gray-200 dark:border-[#334155] rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleSection(sectionKey)}
                          className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] hover:bg-gray-100 dark:hover:bg-[#334155] flex items-center justify-between text-left"
                        >
                          <span className="font-medium text-gray-800 dark:text-white">{sectionConfig.label}</span>
                          {expandedSections[sectionKey] ? (
                            <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                        
                        {expandedSections[sectionKey] && (
                          <div className="p-4 space-y-3 bg-white dark:bg-[#1e293b]">
                            {sectionConfig.actions.map(action => {
                              const isChecked = getEffectivePermission(sectionKey, action.key);
                              const isOverridden = hasOverride(sectionKey, action.key);
                              
                              return (
                                <div key={action.key} className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={(checked) => handlePermissionChange(sectionKey, action.key, checked)}
                                      data-testid={`perm-${sectionKey}-${action.key}`}
                                    />
                                    <span className="text-gray-700 dark:text-gray-200">{action.label}</span>
                                    {isOverridden && (
                                      <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                                        Override
                                      </span>
                                    )}
                                  </div>
                                  {isOverridden && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => resetToRole(sectionKey, action.key)}
                                      className="text-gray-500 dark:text-gray-400 hover:text-red-600 text-xs"
                                    >
                                      Reset to Role
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-[#1e293b] rounded-xl shadow-sm border border-gray-200 dark:border-[#334155] p-12 text-center">
                <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-300 text-lg">Select a user to manage permissions</p>
                <p className="text-gray-400 text-sm mt-2">Choose from the list on the left</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "roles" && (
        <div className="bg-white dark:bg-[#1e293b] rounded-xl shadow-sm border border-gray-200 dark:border-[#334155]">
          <div className="p-4 bg-[#0f172a] text-white flex justify-between items-center">
            <h2 className="font-semibold">System Roles</h2>
            {roleHierarchy?.is_master_admin && (
              <div className="flex gap-2">
                <Input
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="New custom role name"
                  className="w-48 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
                <Button
                  onClick={handleCreateRole}
                  className="bg-[#d4af37] hover:bg-[#b8972e] text-[#0f172a]"
                  data-testid="create-role-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Role
                </Button>
              </div>
            )}
          </div>
          
          <div className="divide-y divide-gray-100 dark:divide-[#334155]">
            {roles.map(role => {
              const roleConfig = ROLE_CONFIG[role.id] || ROLE_CONFIG.operational_agent;
              const RoleIcon = roleConfig?.icon || UserCog;
              
              return (
                <div key={role.id} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#334155]">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${roleConfig?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center`}>
                      <RoleIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        {role.name}
                        {role.is_system && (
                          <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                            System
                          </span>
                        )}
                        {role.protected && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                            Protected
                          </span>
                        )}
                        {role.level && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                            Level {role.level}
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{role.description || "No description"}</p>
                    </div>
                  </div>
                  {!role.is_system && roleHierarchy?.is_master_admin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteRole(role.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-role-${role.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          
          {!roleHierarchy?.is_master_admin && (
            <div className="p-4 bg-gray-50 dark:bg-[#0f172a] border-t border-gray-200 dark:border-[#334155]">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                Only Master Admin can create or delete custom roles
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PermissionsPage;
